import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import InvoiceDetailsLineCard, {
    InvoiceDetailsLineCardStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsLineCard";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField from "@insite/mobius/TextField/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import displayInvoiceFromERP from "@tKgpco/Handlers/Invoices/DisplayInvoiceFromERP";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface InvoiceDetailsSummaryTableStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    titleItem?: GridItemProps;
    title?: TypographyProps;
    linesItem?: GridItemProps;
    linesInnerContainer?: GridContainerProps;
    lineItem?: GridItemProps;
    lineStyles?: InvoiceDetailsLineCardStyles;
    filterContainer?: InjectableCss;
    clearFilterContainer?: InjectableCss;
    noOrdersText?: TypographyPresentationProps;
    noResultsContainer?: InjectableCss;
    erpText?: TypographyPresentationProps;
}

export const summaryTableStyles: InvoiceDetailsSummaryTableStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    titleItem: {
        width: 12,
        css: css`
            padding-bottom: 10px;
        `,
    },
    title: {
        weight: "bold",
        size: "18px",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
            }
            width: 100%;
            padding: 15px 0;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    linesItem: {
        width: 12,
    },
    lineItem: {
        width: 12,
        css: css`
            padding-top: 20px;
            border-bottom: 1px solid ${getColor("common.border")};
            &:first-child {
                padding-top: 10px;
            }
        `,
    },
    filterContainer: {
        css: css`
            background-color: #edf4f8;
            padding: 10px 15px;
            border-radius: 5px;
            width: 100%;
            margin-bottom: 15px;
            margin-top: 25px;
            display: flex;
            align-items: center;

            > div {
                display: flex;
                align-items: center;
                width: 200px;

                margin-right: 20px;

                input[type="number"]::-webkit-inner-spin-button,
                input[type="number"]::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    appearance: none;
                    margin: 0;
                }
            }
        `,
    },
    noOrdersText: {
        weight: "bold",
        size: 16,
        transform: "capitalize",
        css: css`
            display: block;
            margin: 15px auto 0;
        `,
    },
    clearFilterContainer: {
        css: css`
            width: 100%;
            text-align: right;
            margin-bottom: 0px;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
            display: flex;
            flex-direction: column;
            gap: 5px;
            width: 100%;
        `,
    },
    erpText: {
        color: "primary",
        css: css`
            cursor: pointer;
        `,
    },
};

const styles = summaryTableStyles;

const mapDispatchToProps = {
    displayInvoiceFromERP,
};

type Props = ResolveThunks<typeof mapDispatchToProps>;

const InvoiceDetailsSummaryTable = ({ displayInvoiceFromERP }: Props) => {
    const invoiceState = useContext(InvoiceStateContext);
    const invoice = invoiceState.value;
    const [invoiceLines, setInvoiceLines] = React.useState(invoice?.invoiceLines);
    const [custName, setCustName] = React.useState("");
    const [manufacturerItem, setManufacturerItem] = React.useState("");
    const [qtyInvoiced, setQtyInvoiced] = React.useState<number | string>("");

    React.useEffect(() => {
        if (invoice) {
            setInvoiceLines(invoice.invoiceLines);
        }
    }, [invoice]);

    React.useEffect(() => {
        let newInvoiceLines = invoice?.invoiceLines;

        if (custName.trim()) {
            newInvoiceLines = newInvoiceLines?.filter(item =>
                item.productERPNumber?.toLowerCase().includes(custName?.toLowerCase()),
            );
        }
        if (manufacturerItem.trim()) {
            newInvoiceLines = newInvoiceLines?.filter(item =>
                item.manufacturerItem?.toLowerCase().includes(manufacturerItem?.toLowerCase()),
            );
        }
        if (qtyInvoiced || qtyInvoiced === 0) {
            newInvoiceLines = newInvoiceLines?.filter(item => item.qtyInvoiced === qtyInvoiced);
        }

        setInvoiceLines(newInvoiceLines);
    }, [custName, manufacturerItem, qtyInvoiced]);

    const clearFilters = () => {
        setCustName("");
        setManufacturerItem("");
        setQtyInvoiced("");
    };

    const displayInvoiceERP = () => {
        if (!invoiceState.value?.invoiceNumber) {
            return;
        }

        displayInvoiceFromERP({
            invoiceNumber: invoiceState.value?.invoiceNumber,
        });
    };

    if (invoiceState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    return (
        <GridContainer {...styles.container} data-test-selector="invoiceDetail_summaryTable">
            <StyledWrapper {...styles.filterContainer}>
                <StyledWrapper>
                    <TextField
                        label={translate("Cust Part #")}
                        placeholder="item #"
                        value={custName}
                        onChange={e => setCustName(e.target.value)}
                    />
                </StyledWrapper>

                <StyledWrapper>
                    <TextField
                        label={translate("MFG item #")}
                        placeholder="item #"
                        value={manufacturerItem}
                        onChange={e => setManufacturerItem(e.target.value)}
                    />
                </StyledWrapper>

                <StyledWrapper>
                    <TextField
                        label={translate("QTY")}
                        placeholder="QTY"
                        value={qtyInvoiced}
                        type="number"
                        min={0}
                        onChange={e => {
                            const value = parseFloat(e.target.value);
                            // eslint-disable-next-line no-restricted-globals
                            setQtyInvoiced(!isNaN(value) ? value : "");
                        }}
                    />
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.clearFilterContainer}>
                <Button variant="secondary" onClick={clearFilters}>
                    {translate("Clear Filters")}
                </Button>
            </StyledWrapper>

            {invoiceLines?.length ? (
                <>
                    <GridItem {...styles.titleItem}>
                        <Typography {...styles.title}>{translate("Invoice Summary")}</Typography>
                    </GridItem>
                    <GridItem {...styles.linesItem}>
                        <GridContainer {...styles.linesInnerContainer}>
                            {invoiceLines?.map(invoiceLine => (
                                <GridItem
                                    key={invoiceLine.id}
                                    {...styles.lineItem}
                                    data-test-selector={`invoiceDetails_invoiceLine_${invoiceLine.id}`}
                                >
                                    <InvoiceDetailsLineCard
                                        invoiceLine={invoiceLine}
                                        extendedStyles={styles.lineStyles}
                                    />
                                </GridItem>
                            ))}
                        </GridContainer>
                    </GridItem>
                </>
            ) : (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography {...styles.noOrdersText}>{translate("No Invoices Found")}</Typography>
                    {invoiceState.value?.properties?.pullDataManuallyFromErp?.toLowerCase() === "true" && (
                        <Typography {...styles.erpText} onClick={displayInvoiceERP}>
                            {siteMessage("get_data_from_erp_text")}
                        </Typography>
                    )}
                </StyledWrapper>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(InvoiceDetailsSummaryTable),
    definition: {
        group: "Invoice Details",
        displayName: "Summary Table",
        allowedContexts: ["InvoiceDetailsPage"],
    },
};

export default widgetModule;
